import { useEffect, useState } from "react";
import ApproveDenyModal from "../../components/RootElements/ApproveDenyModal";

// Borderless field that only reveals its frame on hover/focus, so the list reads
// as plain text until you actually edit it.
const QUIET_INPUT =
    "bg-transparent border border-transparent rounded px-2 py-1 text-sm min-w-0 " +
    "hover:border-gray-200 focus:border-blue-400 focus:bg-white focus:outline-none";

/* Manage configurable note types (name + color). */
const NoteTypeSettings = ({ noteTypes, onCreate, onUpdate, onDelete }) => {
    const [draft, setDraft] = useState({ name: "", color: "#00AA55", description: "" });
    const [confirmDeleteId, setConfirmDeleteId] = useState(null);
    const [showAdd, setShowAdd] = useState(false);
    // Local edits for existing note types — saves only on blur
    const [localEdits, setLocalEdits] = useState({});

    // Keep localEdits in sync when noteTypes change (e.g. after a save)
    useEffect(() => {
        setLocalEdits(prev => {
            const next = {};
            noteTypes.forEach(nt => {
                next[nt.id] = prev[nt.id] ?? { name: nt.name, color: nt.color, description: nt.description || "" };
            });
            return next;
        });
    }, [noteTypes]);

    const setField = (id, field, value) =>
        setLocalEdits(prev => ({ ...prev, [id]: { ...prev[id], [field]: value } }));

    const saveIfChanged = (nt) => {
        const local = localEdits[nt.id];
        if (!local) return;
        const changed =
            local.name !== nt.name ||
            local.color !== nt.color ||
            local.description !== (nt.description || "");
        if (changed) onUpdate({ ...nt, ...local });
    };

    const submit = (e) => {
        e.preventDefault();
        if (!draft.name.trim() || !draft.color) return;
        onCreate({ name: draft.name.trim(), color: draft.color, description: draft.description });
        setDraft({ name: "", color: "#00AA55", description: "" });
        setShowAdd(false);
    };

    return (
        <>
        <div className="max-w-2xl">

            <ul className="divide-y divide-gray-100 border-y border-gray-100">
                {noteTypes.length === 0 && <p className="text-gray-400 text-sm py-3">Inga noteringstyper.</p>}
                {noteTypes.map(nt => {
                    const local = localEdits[nt.id] ?? { name: nt.name, color: nt.color, description: nt.description || "" };
                    return (
                    <li key={nt.id} className="group flex items-center gap-2 py-1.5">
                        <input type="color" value={local.color}
                            onChange={e => setField(nt.id, "color", e.target.value)}
                            onBlur={() => saveIfChanged(nt)}
                            title="Färg"
                            className="w-5 h-5 rounded cursor-pointer border-none bg-transparent p-0 shrink-0" />
                        <input className={QUIET_INPUT + " w-40 font-medium text-gray-800"} value={local.name}
                            onChange={e => setField(nt.id, "name", e.target.value)}
                            onBlur={() => saveIfChanged(nt)} />
                        <input className={QUIET_INPUT + " flex-1 text-gray-500"} placeholder="Beskrivning" value={local.description}
                            onChange={e => setField(nt.id, "description", e.target.value)}
                            onBlur={() => saveIfChanged(nt)} />
                        <button onClick={() => setConfirmDeleteId(nt.id)}
                            title="Ta bort"
                            aria-label="Ta bort"
                            className="w-6 h-6 shrink-0 rounded flex items-center justify-center text-gray-300 opacity-0 group-hover:opacity-100 focus:opacity-100 hover:text-red-600 hover:bg-red-50 transition">
                            <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                                <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                            </svg>
                        </button>
                    </li>
                    );
                })}
            </ul>

            {showAdd ? (
                <form onSubmit={submit} className="flex items-center gap-2 py-1.5 border-b border-gray-100">
                    <input type="color" value={draft.color}
                        onChange={e => setDraft({ ...draft, color: e.target.value })}
                        title="Färg"
                        className="w-5 h-5 rounded cursor-pointer border-none bg-transparent p-0 shrink-0" />
                    <input autoFocus className={QUIET_INPUT + " w-40 font-medium text-gray-800"} value={draft.name}
                        onChange={e => setDraft({ ...draft, name: e.target.value })} placeholder="t.ex. rengöring" />
                    <input className={QUIET_INPUT + " flex-1 text-gray-500"} placeholder="Beskrivning" value={draft.description}
                        onChange={e => setDraft({ ...draft, description: e.target.value })} />
                    <button type="submit"
                        className="px-2 py-1 rounded text-xs text-blue-600 hover:bg-blue-50 transition">
                        Spara
                    </button>
                    <button type="button" onClick={() => { setShowAdd(false); setDraft({ name: "", color: "#00AA55", description: "" }); }}
                        className="px-2 py-1 rounded text-xs text-gray-400 hover:text-gray-600 transition">
                        Avbryt
                    </button>
                </form>
            ) : (
                <button onClick={() => setShowAdd(true)}
                    className="mt-2 text-xs text-gray-500 hover:text-gray-800 transition">
                    + Lägg till noteringstyp
                </button>
            )}
        </div>
        {confirmDeleteId && (
            <ApproveDenyModal
                onAccept={() => { onDelete(confirmDeleteId); setConfirmDeleteId(null); }}
                onDeny={() => setConfirmDeleteId(null)}
            >
                Vill du verkligen ta bort noteringstypen?
            </ApproveDenyModal>
        )}
        </>
    );
};

export default NoteTypeSettings;
